/**
 * Patches DOM methods to handle Google Translate (and similar browser extensions)
 * that modify the DOM outside of React's control.
 *
 * Google Translate replaces text nodes with <font> wrapper elements.
 * When React tries to remove the original text node, it's no longer a child
 * of the expected parent, causing "Failed to execute 'removeChild' on 'Node'".
 *
 * This patch gracefully handles those cases instead of throwing.
 * Can be removed after upgrading to React 19 (which handles this natively).
 *
 * @see https://github.com/facebook/react/issues/11538
 */
export function patchDomForGoogleTranslate(): void {
  if (typeof Node === "undefined") return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      if (child.parentNode) {
        return originalRemoveChild.call(child.parentNode, child) as T;
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
